import { Alta, ConsultaDni, EstadoAlta, Sincronizacion } from '../../types/Altas';
import { BadgeTone } from '../ui';

/** Cómo se lee cada estado del cruce con Salesforce, en el idioma del jefe comercial. */
export const ESTADO_UI: Record<EstadoAlta, { label: string; tone: BadgeTone; title: string }> = {
  pendiente: {
    label: 'Pendiente',
    tone: 'neutral',
    title:
      'Todavía no se encontró en Salesforce un plan pago para este DNI (si solo tiene el free, se muestra "Plan free"). Se reintenta todos los días.',
  },
  confirmada: {
    label: 'Confirmada',
    tone: 'positive',
    title: 'Salesforce tiene un plan activo (o con el pago pendiente) con el mismo nombre que el cargado.',
  },
  plan_distinto: {
    label: 'Plan distinto',
    tone: 'warning',
    title:
      'La persona tiene un plan pago, pero no el que se cargó. Corregí el plan para que se vuelva a verificar.',
  },
  cliente_previo: {
    label: 'Ya era cliente',
    tone: 'neutral',
    title:
      'Tiene ese plan desde antes del inicio de la campaña, así que no cuenta como venta nueva.',
  },
};

export const ESTADOS: EstadoAlta[] = ['pendiente', 'confirmada', 'plan_distinto', 'cliente_previo'];

/** Días desde un 'YYYY-MM-DD' hasta el día de `hoy` (hora local). */
function diasHasta(fecha: string, hoy: Date): number {
  const [y, m, d] = fecha.split('-').map(Number);
  const inicio = Date.UTC(y, m - 1, d);
  const fin = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((fin - inicio) / 86400000);
}

/**
 * Qué decirle al jefe sobre un DNI antes de cargarlo, según lo que tiene en
 * Salesforce. Null cuando no hay nada que avisar (no tiene ni tuvo plan pago).
 * `aviso` marca lo que conviene mirar antes de cargar.
 */
export function avisoConsultaDni(datos: ConsultaDni, hoy: Date = new Date()): { texto: string; aviso: boolean } | null {
  const { activo, anterior } = datos;
  const quien = datos.nombre ? `${datos.nombre}: ` : '';
  const planTexto = (p: { plan: string | null; desde: string | null }) =>
    `${p.plan ?? 'un plan pago'}${p.desde ? ` desde el ${formatFecha(p.desde)}` : ''}`;
  const anteriorTexto = anterior
    ? `Antes tuvo ${planTexto(anterior)}${anterior.hasta ? ` y lo dio de baja el ${formatFecha(anterior.hasta)}` : ''}.`
    : '';

  if (activo) {
    const previo = activo.desde !== null && activo.desde.slice(0, 10) < datos.campanaDesde;
    if (previo) {
      return {
        texto: `${quien}ya tiene ${planTexto(activo)}, de antes de la campaña: va a quedar como "Ya era cliente".`,
        aviso: true,
      };
    }
    const dias = activo.desde ? diasHasta(activo.desde.slice(0, 10), hoy) : 0;
    const viejo = dias > datos.revisarDiasAntes ? ` Empezó hace ${dias} días: va a quedar para revisar.` : '';
    return {
      texto: `${quien}ya tiene ${planTexto(activo)}. Si la cargás, se confirma con esa fecha.${viejo} ${anteriorTexto}`.trim(),
      aviso: anterior !== null || viejo !== '',
    };
  }
  if (anterior) {
    return { texto: `${quien}${anteriorTexto} Si vuelve a contratar, va a quedar para revisar.`, aviso: true };
  }
  return null;
}

/** Orden de las columnas de planes en el reporte; un plan que no esté acá va después, por nombre. */
const ORDEN_PLANES = ['Executive 3000', 'Active 7000', 'Joven 8000'];

/**
 * "Ventas por plan" como una fila por ejecutivo y una columna por plan, con
 * el total al final. Los planes son los que aparecen en el reporte, en el
 * orden de ORDEN_PLANES.
 */
export function ventasPorEjecutivo(
  porPlan: { ejecutivaId: number; ejecutiva: string; plan: string; cantidad: number }[],
): { planes: string[]; filas: { ejecutivaId: number; ejecutiva: string; porPlan: Record<string, number>; total: number }[] } {
  const planes = Array.from(new Set(porPlan.map((p) => p.plan))).sort((a, b) => {
    const ia = ORDEN_PLANES.indexOf(a);
    const ib = ORDEN_PLANES.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b, 'es');
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  const porEjecutivo = new Map<number, { ejecutivaId: number; ejecutiva: string; porPlan: Record<string, number>; total: number }>();
  porPlan.forEach((p) => {
    const fila = porEjecutivo.get(p.ejecutivaId) ?? { ejecutivaId: p.ejecutivaId, ejecutiva: p.ejecutiva, porPlan: {}, total: 0 };
    fila.porPlan[p.plan] = (fila.porPlan[p.plan] ?? 0) + p.cantidad;
    fila.total += p.cantidad;
    porEjecutivo.set(p.ejecutivaId, fila);
  });
  return { planes, filas: Array.from(porEjecutivo.values()).sort((a, b) => a.ejecutiva.localeCompare(b.ejecutiva, 'es')) };
}

/**
 * Por qué una venta confirmada quedó para que el jefe la mire, en una frase.
 * La app no decide: muestra lo que vio en Salesforce y el jefe resuelve.
 */
export function textoRevisar(alta: Pick<Alta, 'revisar' | 'revisarDetalle' | 'fechaVenta' | 'createdAt'>): string | null {
  if (!alta.revisar) return null;
  const d = alta.revisarDetalle ?? {};
  const partes: string[] = [];
  if (alta.revisar === 'recontratacion' && d.anterior) {
    const a = d.anterior;
    const baja = a.hasta ? ` y lo dio de baja el ${formatFecha(a.hasta)}` : a.estado ? ` (${a.estado})` : '';
    partes.push(`Ya tenía ${a.plan ?? 'un plan pago'}${a.desde ? ` desde el ${formatFecha(a.desde)}` : ''}${baja}.`);
  }
  if (d.diasAntes !== undefined) {
    partes.push(`El plan empezó el ${formatFecha(alta.fechaVenta)}, ${d.diasAntes} días antes de cargar el alta (${formatFechaSqlite(alta.createdAt)}).`);
  }
  return partes.length > 0 ? partes.join(' ') : 'Hay algo para mirar antes de comisionarla.';
}

/** Estilo compartido de inputs y selects del formulario. */
export const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 ' +
  'transition-colors duration-150 ease-out outline-none ' +
  'focus:border-gappex-primary focus:ring-2 focus:ring-gappex-primary/30 ' +
  'disabled:bg-slate-50 disabled:text-slate-400';

export const labelClass =
  'mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400';

/** Encabezado de columna de las tablas de altas. */
export const thClass = 'pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400';

/** Select o input de filtro, en línea con otros. */
export const filtroClass = `${inputClass} w-auto py-1.5`;

/** Deja solo los dígitos del DNI (saca puntos y espacios), como hace el backend. */
export function limpiarDni(texto: string): string {
  return texto.replace(/[.\s]/g, '');
}

export function esDniValido(digitos: string): boolean {
  return /^\d{6,9}$/.test(digitos);
}

/** 'YYYY-MM-DD' o ISO → 'dd/mm/aaaa'. Sin la hora: al jefe comercial le importa el día. */
export function formatFecha(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** Timestamp de SQLite (UTC, 'YYYY-MM-DD HH:MM:SS') → 'dd/mm/aaaa' en la hora de quien mira. */
export function formatFechaSqlite(timestamp: string | null): string {
  if (!timestamp) return '—';
  const d = new Date(timestamp.includes('T') ? timestamp : `${timestamp.replace(' ', 'T')}Z`);
  if (isNaN(d.getTime())) return formatFecha(timestamp);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const dosDigitos = (n: number) => String(n).padStart(2, '0');

/**
 * Período que se comisiona: del 16 del mes anterior al 15 de este. Si todavía
 * no llegó el 16 (el período no cerró), el anterior completo. 28/10 → 16/09 a
 * 15/10; 10/10 → 16/08 a 15/09. Un día cae en un solo período. Es solo el
 * valor inicial de los filtros: el backend recibe las fechas que se eligen.
 */
export function periodoQuincenal(hoy: Date): { desde: string; hasta: string } {
  const mesCierre = hoy.getDate() > 15 ? hoy.getMonth() : hoy.getMonth() - 1;
  const cierre = new Date(hoy.getFullYear(), mesCierre, 15);
  const inicio = new Date(cierre.getFullYear(), cierre.getMonth() - 1, 16);
  const iso = (d: Date) => `${d.getFullYear()}-${dosDigitos(d.getMonth() + 1)}-${dosDigitos(d.getDate())}`;
  return { desde: iso(inicio), hasta: iso(cierre) };
}

function formatFechaHora(iso: string): string {
  // SQLite guarda CURRENT_TIMESTAMP en UTC sin zona; se le agrega para que el navegador lo pase a hora local.
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DISPARADOR: Record<NonNullable<Sincronizacion['disparador']>, string> = {
  carga: 'al cargar',
  manual: 'a pedido',
  diario: 'reintento diario',
};

/** "Última sincronización: 08/10/2026 15:02 (a pedido) · 3 revisadas, 2 confirmadas". */
export function resumenSync(s: Sincronizacion): string {
  const partes = [`${s.revisadas ?? 0} revisadas`];
  if (s.confirmadas) partes.push(`${s.confirmadas} confirmadas`);
  if (s.planDistinto) partes.push(`${s.planDistinto} con plan distinto`);
  if (s.clientePrevio) partes.push(`${s.clientePrevio} ya eran clientes`);
  if (s.pendientes) partes.push(`${s.pendientes} siguen pendientes`);
  const cuando = s.disparador ? ` (${DISPARADOR[s.disparador]})` : '';
  return `Última sincronización: ${formatFechaHora(s.timestamp)}${cuando} · ${partes.join(', ')}`;
}
